/**
 * Seções compartilhadas pelo cadastro e pela edição de contato (visual v2):
 * WhatsApp, e-mail principal, outros contatos e informações adicionais.
 */
import { Info, Mail, MessageCircle, Phone, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { FormField, FormSection } from '@/components/common/FormSection'
import type { Client, Contact } from '@/types/client'

type ClientStatus = Client['status']
type EditableContact = Pick<Contact, 'contactType' | 'value' | 'isPrimary'>

export function WhatsAppSection({ id, value, onChange }: { id: string; value: string; onChange: (value: string) => void }) {
  return (
    <FormSection title="WhatsApp" icon={MessageCircle}>
      <FormField
        label="Número do WhatsApp"
        htmlFor={id}
        hint="Digite o número com DDD. Formatação automática será aplicada ao salvar."
        className="max-w-md"
      >
        <Input id={id} type="tel" value={value} onChange={(e) => onChange(e.target.value)} placeholder="(11) 99999-8888" />
      </FormField>
    </FormSection>
  )
}

export function PrimaryEmailSection({ id, value, onChange }: { id: string; value: string; onChange: (value: string) => void }) {
  return (
    <FormSection title="Email" icon={Mail}>
      <FormField label="Email Principal" htmlFor={id} hint="Email principal para contato com o cliente." className="max-w-md">
        <Input id={id} type="email" value={value} onChange={(e) => onChange(e.target.value)} placeholder="contato@exemplo.com" />
      </FormField>
    </FormSection>
  )
}

interface ContactsSectionProps {
  contacts: EditableContact[]
  onAdd: () => void
  onUpdate: (index: number, field: 'contactType' | 'value' | 'isPrimary', value: unknown) => void
  onRemove: (index: number) => void
}

export function ContactsSection({ contacts, onAdd, onUpdate, onRemove }: ContactsSectionProps) {
  return (
    <FormSection title="Contatos" icon={Phone}>
      {contacts.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {contacts.map((contact, index) => (
            <li key={index} className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center">
              <div className="sm:w-40 sm:shrink-0">
                <NativeSelect
                  aria-label={`Tipo do contato ${index + 1}`}
                  value={contact.contactType}
                  onChange={(e) => onUpdate(index, 'contactType', e.target.value)}
                >
                  <option value="WHATSAPP">WhatsApp</option>
                  <option value="PHONE">Telefone</option>
                  <option value="MOBILE">Celular</option>
                  <option value="ADDRESS">Endereço</option>
                  <option value="OTHER">Outro</option>
                </NativeSelect>
              </div>
              <Input
                type="text"
                aria-label={`Valor do contato ${index + 1}`}
                placeholder="Valor do contato"
                value={contact.value}
                onChange={(e) => onUpdate(index, 'value', e.target.value)}
                className="min-w-0 flex-1"
              />
              <div className="flex items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={contact.isPrimary}
                    onChange={(e) => onUpdate(index, 'isPrimary', e.target.checked)}
                    className="size-4 rounded-sm accent-primary"
                  />
                  Principal
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(index)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-3.5" /> Remover
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Nenhum contato adicionado</p>
      )}
      <div>
        <Button type="button" variant="outline" size="sm" onClick={onAdd}>
          <Plus className="size-4" /> Adicionar Contato
        </Button>
      </div>
    </FormSection>
  )
}

interface AdditionalInfoSectionProps {
  statusId: string
  notesId: string
  status: ClientStatus
  notes: string
  onStatusChange: (status: ClientStatus) => void
  onNotesChange: (notes: string) => void
}

export function AdditionalInfoSection({ statusId, notesId, status, notes, onStatusChange, onNotesChange }: AdditionalInfoSectionProps) {
  return (
    <FormSection title="Informações Adicionais" icon={Info}>
      <FormField label="Status" htmlFor={statusId}>
        <NativeSelect id={statusId} value={status} onChange={(e) => onStatusChange(e.target.value as ClientStatus)}>
          <option value="ACTIVE">Ativo</option>
          <option value="PROSPECT">Prospect</option>
          <option value="INACTIVE">Inativo</option>
          <option value="BLOCKED">Bloqueado</option>
        </NativeSelect>
      </FormField>
      <FormField label="Observações" htmlFor={notesId}>
        <Textarea
          id={notesId}
          rows={4}
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Observações sobre o contato..."
        />
      </FormField>
    </FormSection>
  )
}
